const responsiveDesign = {
    name: "Responsive Design",
    path: "responsive-design",
    title: "Responsive Design",
    description: "Using responsive utility variants to build adaptive user interfaces.",
    heading: false,
    exampleData: [
        {
            "title": "",
            "description": "Every utility class in Unicss can be applied conditionally at different breakpoints, which makes it a piece of cake to build complex responsive interfaces without ever leaving your HTML.",
            "moreDescription": "First, make sure you've added the viewport meta tag to the <head> of your document:",
            "parentClass": "!bg-none !border-0",
            "example": {
                "element": "div",
                "className": "relative",
                "code": `<meta name="viewport" content="width=device-width, initial-scale=1.0" />`
            }
        },
        {
            "title": "",
            "description": "Then to add a utility but only have it take effect at a certain breakpoint, all you need to do is prefix the utility with the breakpoint name, followed by the : character:",
            "moreDescription": "First, make sure you've added the viewport meta tag to the <head> of your document:",
            "parentClass": "!bg-none !border-0",
            "example": {
                "element": "div",
                "className": "relative",
                "code": `<!-- Width of 16 by default, 32 on medium screens, and 48 on large screens -->
<img class="w-16 md:w-32 lg:w-48" src="..." />`
            },
            "moreexample": `<div class="mt-10 text-color-gray-800 dark:text-color-gray-300">
  <p class="mb-6">
    There are five breakpoints by default, inspired by common device resolutions:
  </p>

  <div class="overflow-x-auto">
    <table class="min-w-full border border-color-gray-200 text-left text-sm">
      <thead class="bg-gray-100 text-gray-700">
        <tr>
          <th class="px-4 py-2 border-b border-color-gray-200">Breakpoint prefix</th>
          <th class="px-4 py-2 border-b border-color-gray-200">Minimum width</th>
          <th class="px-4 py-2 border-b border-color-gray-200">CSS</th>
        </tr>
      </thead>
      <tbody>
        <tr class="border-t">
          <td class="px-4 py-2 text-[15px] font-mono text-blue-600 border-b border-color-gray-200">sm</td>
          <td class="px-4 py-2 border-b border-color-gray-200">40rem <span class="text-gray-500 text-sm">(640px)</span></td>
          <td class="px-4 py-2 font-mono text-sm text-gray-700 border-b border-color-gray-200">@media (width &gt;= 40rem) { ... }</td>
        </tr>
        <tr class="border-t">
          <td class="px-4 py-2 text-[15px] font-mono text-blue-600 border-b border-color-gray-200">md</td>
          <td class="px-4 py-2 border-b border-color-gray-200">48rem <span class="text-gray-500 text-sm">(768px)</span></td>
          <td class="px-4 py-2 font-mono text-sm text-gray-700 border-b border-color-gray-200">@media (width &gt;= 48rem) { ... }</td>
        </tr>
        <tr class="border-t">
          <td class="px-4 py-2 text-[15px] font-mono text-blue-600 border-b border-color-gray-200">lg</td>
          <td class="px-4 py-2 border-b border-color-gray-200">64rem <span class="text-gray-500 text-sm">(1024px)</span></td>
          <td class="px-4 py-2 font-mono text-sm text-gray-700 border-b border-color-gray-200">@media (width &gt;= 64rem) { ... }</td>
        </tr>
        <tr class="border-t">
          <td class="px-4 py-2 text-[15px] font-mono text-blue-600 border-b border-color-gray-200">xl</td>
          <td class="px-4 py-2 border-b border-color-gray-200">80rem <span class="text-gray-500 text-sm">(1280px)</span></td>
          <td class="px-4 py-2 font-mono text-sm text-gray-700 border-b border-color-gray-200">@media (width &gt;= 80rem) { ... }</td>
        </tr>
        <tr class="border-t">
          <td class="px-4 py-2 text-[15px] font-mono text-blue-600">2xl</td>
          <td class="px-4 py-2">96rem <span class="text-gray-500 text-sm">(1536px)</span></td>
          <td class="px-4 py-2 font-mono text-sm text-gray-700">@media (width &gt;= 96rem) { ... }</td>
        </tr>
      </tbody>
    </table>
  </div>

  <p class="mt-6 text-gray-700">
    This works for <span class="font-semibold">every utility class in the framework</span>, which means you can change literally anything at a given breakpoint — even things like letter spacing or cursor styles.
  </p>
</div>
`
        },
        {
            "title": "Don't use sm: to target mobile devices",
            "description": "",
            "moreDescription": "This will only center text on screens 640px and wider, not on small screens",
            "parentClass": "!bg-none !border-0",
            "example": {
                "element": "div",
                "className": "relative",
                "code": `<div class="sm:text-center"></div>`
            }
        },
        {
            "title": "Use unprefixed utilities to target mobile, and override them at larger breakpoints",
            "description": "",
            "moreDescription": "This will center text on mobile, and left align it on screens 640px and wider",
            "parentClass": "!bg-none !border-0",
            "example": {
                "element": "div",
                "className": "relative",
                "code": `<div class="text-center sm:text-left"></div>`
            }
        },
    ]
};

export default responsiveDesign;
<div class="overflow-x-auto sr-only">
    <table class="min-w-full border border-color-gray-200 text-left text-sm">
      <thead class="bg-gray-100 text-gray-700">
        <tr>
          <th class="px-4 py-2 border-b">Breakpoint prefix</th>
          <th class="px-4 py-2 border-b">Minimum width</th>
          <th class="px-4 py-2 border-b">CSS</th>
        </tr>
      </thead>
      <tbody>
        <tr class="border-t">
          <td class="px-4 py-2 text-[15px] font-mono text-blue-600">sm</td>
          <td class="px-4 py-2">40rem <span class="text-gray-500 text-sm">(640px)</span></td>
          <td class="px-4 py-2 font-mono text-sm text-gray-700">@media (width &gt;= 40rem)</td>
        </tr>
        <tr class="border-t">
          <td class="px-4 py-2 text-[15px] font-mono text-blue-600">md</td>
          <td class="px-4 py-2">48rem <span class="text-gray-500 text-sm">(768px)</span></td>
          <td class="px-4 py-2 font-mono text-sm text-gray-700">@media (width &gt;= 48rem)</td>
        </tr>
        <tr class="border-t">
          <td class="px-4 py-2 text-[15px] font-mono text-blue-600">lg</td>
          <td class="px-4 py-2">64rem <span class="text-gray-500 text-sm">(1024px)</span></td>
          <td class="px-4 py-2 font-mono text-sm text-gray-700">@media (width &gt;= 64rem)</td>
        </tr>
        <tr class="border-t">
          <td class="px-4 py-2 text-[15px] font-mono text-blue-600">xl</td>
          <td class="px-4 py-2">80rem <span class="text-gray-500 text-sm">(1280px)</span></td>
          <td class="px-4 py-2 font-mono text-sm text-gray-700">@media (width &gt;= 80rem)</td>
        </tr>
        <tr class="border-t">
          <td class="px-4 py-2 text-[15px] font-mono text-blue-600">2xl</td>
          <td class="px-4 py-2">96rem <span class="text-gray-500 text-sm">(1536px)</span></td>
          <td class="px-4 py-2 font-mono text-sm text-gray-700">@media (width &gt;= 96rem)</td>
        </tr>
      </tbody>
    </table>
  </div>